<template>
  <DsCard variant="outlined" padding="md" class="w-full">
    <h4 class="text-[1rem] leading-[1.5] text-primary font-inter font-semibold mb-4">
      {{ cardTitle }}
    </h4>
    <div class="w-full flex flex-col gap-4">
      <div class="w-full">
        <label
          for="dateOfBirth"
          class="text-base text-primary font-inter font-medium block mb-1 leading-5"
          >Date of Birth</label
        >
        <DsDatePicker
          id="dateOfBirth"
          name="dateOfBirth"
          v-model="data.dateOfBirth"
          display-format="DD-MM-YYYY"
          placeholder="Enter your Date of Birth"
          :max="today"
          :disabled="fieldDisable"
          :error="!fieldDisable && formErrors.dateOfBirth ? ERROR_VALID_DOB : ''"
        />
      </div>
      <div class="w-full">
        <label
          for="partialNric"
          class="text-base text-primary font-inter font-medium block mb-1 leading-5"
          >Last 4 chars of NRIC/FIN/Passport no.</label
        >
        <DsInput
          id="partialNric"
          name="partialNric"
          v-model="data.partialNric"
          placeholder="Eg: 123A"
          :max-length="4"
          :disabled="fieldDisable"
          :error="!fieldDisable && formErrors.partialNric ? ERROR_VALID_NRIC : ''"
        />
      </div>
      <DsLink size="sm" underline="always" class="self-start" @click="onInfo"
        >What is this information used for?</DsLink
      >
      <div v-if="isRequested" class="w-full flex gap-[8px]">
        <DsButton variant="borderless" size="48" class="flex-1" @click="onRequestCancel"
          >Cancel</DsButton
        >
        <DsButton
          variant="bordered"
          shape="pill"
          size="48"
          class="flex-1"
          :loading="loading"
          :disabled="disabledSubmit"
          @click="handleSubmit"
          >Submit</DsButton
        >
      </div>
      <DsButton v-else variant="bordered" shape="pill" size="48" class="w-full" @click="onRequest">
        {{ requestText }}
        <DsIcon name="edit" size="sm" class="ms-1" />
      </DsButton>
    </div>
  </DsCard>
  <ModalSheet v-model="usedInfo" :removeSpace="true">
    <h4 class="text-2xl text-[#2B2926] mb-8 font-primary font-black tracking-[-0.03em]">
      What is this information used for?
    </h4>
    <p class="text-base font-medium font-inter text-[#433D31] mb-4">
      We use your Partial NRIC to make sure your giving is accurately reflected in your giving
      records for your viewing.
    </p>
    <p class="text-base font-medium font-inter text-[#433D31] pb-[50%]">
      Your information is kept secured and only used for this purpose.
    </p>
  </ModalSheet>
</template>
<script>
import ModalSheet from '@/components/common/ModalSheet.vue';
import { mapState } from 'vuex';
import axios from 'axios';
import dayjs from 'dayjs';
import { REGEX_NRIC, REGEX_DOB } from '@/constants/validation/regex-patterns';
import { ERROR_VALID_NRIC } from '@/constants/validation/error-messages';
import { personalIdUpdatedTitle } from '@/utils/member-split/unlinked-profile-copy';
import { isLinkRequired, LINK_REQUIRED_MESSAGE } from '@/utils/member-split/link-required';
import { isPersonalIdInUse, personalIdInUseMessage } from '@/utils/giving-login';
import {
  Card as DsCard,
  Button as DsButton,
  DatePicker as DsDatePicker,
  Icon as DsIcon,
  Input as DsInput,
  Link as DsLink,
} from '@team-hogc/hogc-one-design-system';

const ERROR_VALID_DOB = 'Enter a valid date of birth, not in the future';
const DEFAULT_TITLE = 'Request for an update if these fields are incorrect.';
const MEMBER_EDIT_TITLE =
  "Please provide the accurate details, and we'll get back to you via email.";
// A login waiting for its leader's link corrects its own registration: no email follows.
const UNLINKED_EDIT_TITLE = 'Please provide the accurate details.';
const MEMBER_RECEIVED_TITLE = "Request received ✅. We'll review it and email you shortly!";
const REQUEST_FAILED_MESSAGE = "We couldn't send your request. Please try again.";

export default {
  components: {
    ModalSheet,
    DsCard,
    DsButton,
    DsDatePicker,
    DsIcon,
    DsInput,
    DsLink,
  },
  props: {
    payload: {
      type: Object,
      default: () => ({}),
    },
    showError: {
      type: Object,
      default: () => ({}),
    },
    scrollElementId: {
      type: String,
    },
  },
  emits: ['hideFooter', 'profileRefreshed'],
  data() {
    return {
      requestText: 'Request for an update',
      usedInfo: false,
      isRequested: false,
      fieldDisable: true,
      cardTitle: DEFAULT_TITLE,
      // Set once a login waiting for its link has corrected its personal id: the card
      // keeps saying so when the page hands it the refreshed payload.
      updatedTitle: '',
      disabledSubmit: false,
      loading: false,
      ERROR_VALID_DOB,
      ERROR_VALID_NRIC,
      // The date picker's upper bound, as YYYY-MM-DD in local time.
      today: dayjs().format('YYYY-MM-DD'),
      data: {
        dateOfBirth: '',
        partialNric: '',
      },
      formErrors: {
        dateOfBirth: false,
        partialNric: false,
      },
    };
  },
  computed: {
    ...mapState('user', ['token']),
    hasNoMember() {
      return this.$store.state.profile?.hasNoMember === true;
    },
    restingTitle() {
      return this.updatedTitle || DEFAULT_TITLE;
    },
    // true if *either* child validation or parent says there's an error
    hasErrors() {
      const pe = this.showError || {};
      const ce = this.formErrors || {};
      return pe.dateOfBirth || pe.partialNric || ce.dateOfBirth || ce.partialNric;
    },
  },
  created() {
    Object.assign(this.data, {
      dateOfBirth: this.payload.dateOfBirth || '',
      partialNric: this.payload.partialNric || '',
    });
  },
  methods: {
    onInfo() {
      this.usedInfo = !this.usedInfo;
    },
    onRequest() {
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      if (this.fieldDisable) {
        this.cardTitle = this.restingTitle;
      } else {
        this.cardTitle = this.hasNoMember ? UNLINKED_EDIT_TITLE : MEMBER_EDIT_TITLE;
      }
    },
    async onRequestSubmit() {
      const before = {
        dateOfBirth: this.payload.dateOfBirth,
        partialNric: this.payload.partialNric,
      };
      try {
        this.payload.dateOfBirth = this.data.dateOfBirth;
        this.payload.partialNric = this.data.partialNric;
        this.loading = true;
        this.cardTitle = 'Submitting Request...';
        await axios.post(
          process.env.VUE_APP_API_URL + '/v2/profile/pid-update-request',
          {
            dateOfBirth: this.data.dateOfBirth,
            partialNric: this.data.partialNric,
          },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          },
        );
        this.loading = false;
      } catch (e) {
        // Nothing was sent: put the profile's values back and stay in the form, so the
        // request can be sent again.
        Object.assign(this.payload, before);
        this.loading = false;
        // The API's own text here is internal ("Failed to trigger Pid Update").
        // A login with no member whose corrected personal id is taken: the server's words.
        const message = isLinkRequired(e)
          ? LINK_REQUIRED_MESSAGE
          : isPersonalIdInUse(e)
            ? personalIdInUseMessage(e)
            : REQUEST_FAILED_MESSAGE;
        this.$store.dispatch('toast/error', message);
        return;
      }
      this.isRequested = !this.isRequested;
      this.fieldDisable = !this.fieldDisable;
      if (this.hasNoMember) {
        await this.showCorrected();
        return;
      }
      this.requestText = 'Edit request';
      this.cardTitle = MEMBER_RECEIVED_TITLE;
    },
    // The API has already rewritten the registration: refresh the profile so the new
    // personal id shows, and say it is done. A failed refresh still says so, without the id.
    async showCorrected() {
      try {
        await this.$store.dispatch('profile/forceFetchProfile');
      } catch (e) {
        console.error('Profile refresh after personal id correction failed:', e);
      }
      this.updatedTitle = personalIdUpdatedTitle(this.$store.state.profile?.personalId);
      this.cardTitle = this.updatedTitle;
      this.$emit('profileRefreshed');
    },
    handleSubmit() {
      // optional: also ensure fields are present
      const missing = !this.data.dateOfBirth || !this.data.partialNric;

      if (this.hasErrors || missing) {
        this.disabledSubmit = true;
        return;
      }
      this.disabledSubmit = false;
      this.onRequestSubmit();
    },
    onRequestCancel() {
      this.requestText = 'Request for an update';
      this.isRequested = false;
      this.fieldDisable = true;
      this.cardTitle = this.restingTitle;

      Object.assign(this.data, {
        dateOfBirth: this.payload.dateOfBirth || '',
        partialNric: this.payload.partialNric || '',
      });
    },
    isValidNRIC(value) {
      if (!value) return true; // Prevents errors on empty values
      return REGEX_NRIC.test(value);
    },
    isValidDOB(value) {
      // Validate DD-MM-YYYY format using regex
      if (!REGEX_DOB.test(value)) {
        return false; // Format is incorrect
      }

      // Extract day, month, and year
      const [day, month, year] = value.split('-').map(Number);

      // Ensure correct date conversion (months in JS Date start from 0)
      const inputDate = new Date(year, month - 1, day);
      const today = new Date();

      // Remove time from today's date for accurate comparison
      today.setHours(0, 0, 0, 0);
      inputDate.setHours(0, 0, 0, 0);

      // Ensure input date is not in the future
      const isValid = inputDate <= today;

      return isValid;
    },
    formatDateToDDMMYYYY(value) {
      const date = new Date(value);
      const day = `0${date.getDate()}`.slice(-2);
      const month = `0${date.getMonth() + 1}`.slice(-2);
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
  },
  watch: {
    // Prefill & keep in sync with payload
    payload: {
      deep: true,
      immediate: true,
      handler(newVal = {}) {
        Object.assign(this.data, {
          dateOfBirth: newVal.dateOfBirth || '',
          partialNric: newVal.partialNric || '',
        });
        this.cardTitle =
          this.updatedTitle || 'Request received ✅. We’ll review it and email you shortly!';

        this.$nextTick(() => {
          this.cardTitle = this.restingTitle;
        });
      },
    },
    // Live validation -> local error state
    'data.dateOfBirth'(v) {
      this.formErrors.dateOfBirth = !this.isValidDOB(this.formatDateToDDMMYYYY(v));
    },
    'data.partialNric'(v) {
      this.formErrors.partialNric = !this.isValidNRIC(v);
    },
    // Drive submit button disabled state from local errors
    formErrors: {
      deep: true,
      handler(e) {
        this.disabledSubmit = e.dateOfBirth || e.partialNric;
      },
    },
    // Handle sheet open/close & footer
    usedInfo(newVal) {
      const el = document.getElementById(this.scrollElementId);
      if (el) el.scrollY = newVal ? false : true;
      this.$emit('hideFooter', this.usedInfo);
    },
  },
};
</script>
